import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product } from '../types';
import { getImageUrl } from '../config/constants';
import { useWishlist } from '../context';

interface ProductCardProps {
  product: Product;
  onAddToWishlist?: (_productId: number) => void;
  onQuickView?: (_product: Product) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onAddToWishlist, onQuickView }) => {
  const navigate = useNavigate();
  const { isInWishlist: checkIsInWishlist, addItem, removeItem } = useWishlist();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const isInWishlist = checkIsInWishlist(product.id);
  // Get all images from the product
  const productImages = (() => {
    const images = product.images;
    if (images && Array.isArray(images) && images.length > 0) {
      return images
        .filter((img: any) => img.type === 'image')
        .map((img: any) => getImageUrl(img.url));
    }
    return product.image_url ? [getImageUrl(product.image_url)] : [];
  })();

  const productImage = productImages[currentImageIndex] || productImages[0] || '';

  // Get collection name or category
  const category = product.collection_name || product.collection?.name || 'Productos';

  // Calculate effective price (with discount if available)
  const effectivePrice = product.discount_price || product.price;
  const hasDiscount = product.discount_price && product.discount_price < product.price;

  // Stock status
  const isOutOfStock = product.stock_quantity === 0;
  const isLowStock = product.stock_quantity > 0 && product.stock_quantity <= 5;

  // Truncate description
  const truncatedDescription = product.description
    ? product.description.length > 80
      ? product.description.substring(0, 80) + '...'
      : product.description
    : '';

  // Handle wishlist toggle
  const handleWishlistToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isInWishlist) {
      removeItem(product.id);
    } else {
      addItem(product);
    }

    // Optional callback for additional handling
    if (onAddToWishlist) {
      onAddToWishlist(product.id);
    }
  };

  // Handle quick view
  const handleQuickView = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onQuickView) {
      onQuickView(product);
    }
  };

  // Handle image hover for multi-image products
  const handleMouseEnter = () => {
    if (productImages.length > 1) {
      setCurrentImageIndex(1);
    }
  };

  const handleMouseLeave = () => {
    setCurrentImageIndex(0);
  };

  // Navigate to product detail page
  const handleProductClick = () => {
    navigate(`/products/${product.id}`);
  };

  return (
    <div
      onClick={handleProductClick}
      className="group bg-canvas-primary rounded-2xl overflow-hidden shadow-card transition-all duration-500 hover:shadow-card-hover hover:-translate-y-2 cursor-pointer"
    >
      <div
        className="relative pt-[120%] overflow-hidden bg-canvas-secondary"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {productImage ? (
          <img
            src={productImage}
            alt={product.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <svg className="w-20 h-20 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

        {/* Top badges */}
        <div className="absolute top-4 right-4 flex flex-col gap-2">
          {hasDiscount && (
            <div className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-bold">
              -{Math.round(product.discount_percent ?? 0)}%
            </div>
          )}
          {isOutOfStock && (
            <div className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
              Agotado
            </div>
          )}
          {isLowStock && !isOutOfStock && (
            <div className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold">
              ¡Últimas {product.stock_quantity}!
            </div>
          )}
        </div>

        {/* Wishlist button */}
        <button
          onClick={handleWishlistToggle}
          className="absolute top-4 left-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-all duration-300 shadow-lg hover:scale-110"
          aria-label="Add to wishlist"
        >
          <svg
            className={`w-5 h-5 transition-colors duration-300 ${isInWishlist ? 'fill-red-500 text-red-500' : 'text-gray-600'}`}
            fill={isInWishlist ? 'currentColor' : 'none'}
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </button>

        {/* Quick View and Details Buttons - Appears on Hover */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 translate-y-16 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 flex gap-2">
          {onQuickView && (
            <button
              onClick={handleQuickView}
              className="bg-white hover:bg-fg-highlight-primary text-fg-primary hover:text-white font-bold text-xs uppercase tracking-wide px-6 py-2.5 rounded-full shadow-xl whitespace-nowrap flex items-center gap-2 transition-colors duration-300"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Vista Rápida
            </button>
          )}
          <div
            onClick={handleProductClick}
            className="bg-canvas-secondary hover:bg-fg-highlight-primary text-fg-primary hover:text-white font-bold text-xs uppercase tracking-wide px-6 py-2.5 rounded-full shadow-xl whitespace-nowrap flex items-center gap-2 transition-colors duration-300 cursor-pointer"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Detalles
          </div>
        </div>
      </div>
      <div className="p-8">
        <div className="mb-4">
          <span className="inline-flex items-center gap-1.5 text-[10px] text-fg-highlight-primary uppercase tracking-[0.15em] font-bold bg-gradient-to-r from-canvas-secondary to-canvas-secondary/50 px-3 py-1.5 rounded-md border border-fg-highlight-primary/20 shadow-sm hover:shadow-md transition-all duration-300">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            {category}
          </span>
        </div>
        <h3 className="text-xl font-bold mb-3 text-fg-primary group-hover:text-fg-highlight-primary transition-colors duration-300 break-words line-clamp-2">
          {product.name}
        </h3>

        {/* Product description preview */}
        {truncatedDescription && (
          <p className="text-sm text-fg-secondary mb-4 leading-relaxed">
            {truncatedDescription}
          </p>
        )}

        <div className="flex flex-col gap-1">
          {hasDiscount && (
            <p className="text-base font-semibold text-fg-tertiary line-through">
              {new Intl.NumberFormat('es-CO', {
                style: 'currency',
                currency: 'COP',
                maximumFractionDigits: 0,
              }).format(product.price)}
            </p>
          )}
          <p className={`text-xl font-bold ${hasDiscount ? 'text-green-600' : 'text-fg-highlight-primary'} break-words`}>
            {new Intl.NumberFormat('es-CO', {
              style: 'currency',
              currency: 'COP',
              maximumFractionDigits: 0,
            }).format(effectivePrice)}
          </p>
        </div>

        {/* Stock indicator */}
        <div className="mt-4">
          {isOutOfStock ? (
            <div className="flex items-center gap-2 text-red-600">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-xs font-semibold">Sin stock</span>
            </div>
          ) : isLowStock ? (
            <div className="flex items-center gap-2 text-orange-500">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-xs font-semibold">Stock bajo</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-green-600">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-xs font-semibold">En stock</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
